import { Entity, Site } from '@debbie/common/dist/index.js';
import { InteractionType, } from '@debbie/common/dist/interactions/index.js';
import { getTemplateTypeForInteractionType } from '@debbie/common/dist/templates/index.js';
import { toEditorMentions } from '@debbie/common/dist/templates/suggestions.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCustomer } from '@debbie/cortex/dist/customers/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useNewInteraction } from '@debbie/cortex/dist/newInteractions/index.js';
import { endedWritingClientNote, endedWritingDebtorNote, startedWritingClientNote, startedWritingDebtorNote, } from '@debbie/cortex/dist/socket.js';
import { useMentionSuggestions } from '@debbie/cortex/dist/templates/index.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { File as FileBadge } from '../../components/index.js';
import { parseText } from '../../utils/templates.js';
import NewEditor from '../editor/Editor.js';
import { isEditorEmpty, useEditorState } from '../editor/hooks.js';
import FileDropzone from '../FileDropzone.js';
import ClientNoteRecipients from './ClientNoteRecipients.js';
import InteractionFooter, { AttachAction, SendButton, } from './InteractionFooter.js';
import TemplateSearch from './TemplateSearch.js';
import VisibilityToggle from './VisibilityToggle.js';
const NoteInteraction = (props) => {
    const { referenceId, id, options, sendButtonText } = props;
    const site = useSelector((s) => s.site);
    const [interaction, setInteraction] = useNewInteraction(referenceId);
    const note = interaction.note;
    const uploadedFiles = interaction.uploadedFiles;
    const isCaseworker = site === Site.Caseworker;
    const isDebtor = site === Site.Debtor;
    const showMentionsAndTemplates = isCaseworker && props.referenceType === Entity.Case;
    const showToolbar = !isDebtor;
    const templateType = getTemplateTypeForInteractionType(options.type);
    const isClientNoteOnCase = isCaseworker &&
        options.type === InteractionType.ClientNote &&
        props.referenceType === Entity.Case;
    const singleCaseLoader = useCase(props.referenceType === Entity.Case &&
        (showMentionsAndTemplates || isClientNoteOnCase)
        ? referenceId
        : undefined);
    const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null;
    const customerLoader = useCustomer(singleCase?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const creditorLoader = useCreditor(isClientNoteOnCase ? singleCase?.creditorId : undefined);
    const creditor = h.hasData(creditorLoader) ? creditorLoader.data : null;
    // The caseworker's explicit selection wins; until then default to the
    // creditor's contact (null while the creditor is still loading).
    const [pickedRecipientIds, setPickedRecipientIds] = useState(null);
    const recipientIds = pickedRecipientIds ??
        (creditor ? (creditor.contactId != null ? [creditor.contactId] : []) : null);
    const mentionSuggestions = useMentionSuggestions(showMentionsAndTemplates ? templateType : null, singleCase?.caseGroup?.id || null);
    const editorMentions = useMemo(() => toEditorMentions(mentionSuggestions), [mentionSuggestions]);
    const [visibleToClients, setVisibleToClients] = useState(InteractionType.Note === options.type ? false : true);
    const bodyState = useEditorState(note?.content || '<p></p>');
    const dropzoneRef = useRef(null);
    const showVisibilityToggle = ![InteractionType.DebtorNote, InteractionType.ClientNote].includes(options.type) && props.referenceType === Entity.Case;
    const hasBody = () => !isEditorEmpty(bodyState.html);
    // Broadcast a "currently writing" presence activity while a creditor/debtor
    // message is being composed, so the other party sees it (rendered by
    // ActiveUsers). The ref guards against emitting "started" repeatedly while the
    // editor keeps focus.
    const isWritingRef = useRef(false);
    const startWriting = () => {
        if (id == null || isWritingRef.current) {
            return;
        }
        if (options.type === InteractionType.ClientNote) {
            isWritingRef.current = true;
            startedWritingClientNote(`${id}`);
        }
        else if (options.type === InteractionType.DebtorNote) {
            isWritingRef.current = true;
            startedWritingDebtorNote(`${id}`);
        }
    };
    const endWriting = () => {
        if (id == null || !isWritingRef.current) {
            return;
        }
        isWritingRef.current = false;
        if (options.type === InteractionType.ClientNote) {
            endedWritingClientNote(`${id}`);
        }
        else if (options.type === InteractionType.DebtorNote) {
            endedWritingDebtorNote(`${id}`);
        }
    };
    // Make sure the activity is cleared if the composer unmounts while focused.
    useEffect(() => endWriting, []);
    const sendNoteMutation = useMutation(async () => {
        if (props.textAreaDisabled) {
            throw new Error('Cannot send note when text area is disabled');
        }
        if (props.onBeforeSend) {
            const proceed = await props.onBeforeSend();
            if (proceed === false) {
                return;
            }
        }
        const parsedContent = mentionSuggestions.length
            ? parseText(bodyState.html, mentionSuggestions)
            : bodyState.html;
        const details = isClientNoteOnCase
            ? { recipients: recipientIds ?? [] }
            : {};
        await c.newInteractions.sendNote(referenceId, props.referenceType, options, { ...note, content: parsedContent }, uploadedFiles, visibleToClients ? 'client' : 'caseworker', details);
        endWriting();
        bodyState.overwriteEditorContent('<p></p>');
        if (props.continueCase) {
            props.continueCase(referenceId, parseInt(`${id}`));
        }
    });
    if (!note) {
        return null;
    }
    const openDropzone = () => {
        dropzoneRef.current?.open();
    };
    const removeFile = (index) => {
        setInteraction('uploadedFiles', uploadedFiles.slice(0, index).concat(uploadedFiles.slice(index + 1)));
    };
    // Recipients are optional — clients also read messages in the creditor web,
    // so a note may deliberately be sent without notifying anyone. Only block
    // while the creditor (and thereby the default recipient) is still loading.
    const sendDisabled = !hasBody() ||
        props.textAreaDisabled ||
        props.disableSend ||
        (isClientNoteOnCase && recipientIds == null);
    return (React.createElement(FileDropzone, { onChange: (file) => {
            setInteraction('uploadedFiles', uploadedFiles.concat([file]));
        }, dropzoneRef: dropzoneRef },
        props.extraFields,
        isClientNoteOnCase && creditor && (React.createElement(ClientNoteRecipients, { creditorId: creditor.creditorId, selectedIds: recipientIds ?? [], onChange: setPickedRecipientIds })),
        showMentionsAndTemplates && templateType && singleCase && customer && (React.createElement(TemplateSearch, { templateType: templateType, case: singleCase, customer: customer, onChange: (content) => {
                bodyState.overwriteEditorContent(content);
            } })),
        React.createElement(NewEditor, { hideControls: !showToolbar, mentions: editorMentions, mentionSuggestions: mentionSuggestions, initialContent: bodyState.editorContent, placeholder: props.textAreaPlaceholder, minHeight: props.editorMinHeight, onChange: (html) => {
                bodyState.onChange(html);
            }, onFocus: startWriting, onBlur: endWriting, disabled: props.textAreaDisabled }),
        uploadedFiles.length > 0 && (React.createElement(AttachmentsBar, null, uploadedFiles.map((file, i) => (React.createElement(FileBadge, { key: file.fileId, file: file, onRemove: () => removeFile(i), removable: true }))))),
        React.createElement(InteractionFooter, { error: sendNoteMutation.error, primary: React.createElement(SendButton, { loading: note.loading || sendNoteMutation.isLoading, disabled: sendDisabled, onClick: () => sendNoteMutation.mutate() }, sendButtonText), actions: React.createElement(React.Fragment, null,
                React.createElement(AttachAction, { onClick: openDropzone }),
                showVisibilityToggle && (React.createElement(VisibilityToggle, { visible: visibleToClients, onChange: setVisibleToClients }))) })));
};
export default NoteInteraction;
const AttachmentsBar = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 12px 0 12px;
  border-top: 1px solid #eef0f5;
`;
//# sourceMappingURL=NoteInteraction.js.map